import { getGeoExtendQuery } from '@openx/components-targeting/utils/hooks/useSearchGeoLocations.ts';
import type { GeoLocation, GeoLocationsList } from '@openx/types/geoLocation.ts';
import type { GeoSearchOutput } from '@openx/types/gqlTypes.ts';
import type { UseFetchBySearchTextProps } from '@openx/types/targeting/targetingHookTypes.ts';
import type { TargetingProviderConfig } from '@openx/types/targeting/targetingProviderConfig.ts';
import { QueryKeys } from '@openx/utils/api/queryKeys.ts';
import { buildGeographyEntitiesSearchObject, getSearchQuery } from '@openx/utils/state/geoLocationSearch/utils.ts';
import { useQuery } from '@tanstack/react-query';
import { GeoSearchDocument } from '../../../../graphql/query/geo/search.generated.tsx';
import { getGraphQLRequestFromClient } from '../../../../modules/graphql/types.ts';

type GeoSearchRefetchFn = () => Promise<unknown>;

export const useFetchBySearchText: TargetingProviderConfig['useFetchBySearchText'] = ({
  searchText,
  excludeArea = [],
  includeArea = [],
  radius = false,
  geographicType,
}: UseFetchBySearchTextProps) => {
  const searchObject = buildGeographyEntitiesSearchObject(`${searchText}`, radius, geographicType);

  const phrase = getSearchQuery(searchObject, false, false) + getGeoExtendQuery({ excludeArea, includeArea });
  const variables = { isRawQuery: true, phrase };

  const {
    data,
    isLoading: loading,
    refetch,
  } = useQuery({
    queryKey: [QueryKeys.SEARCH, 'geo', variables],
    queryFn: ({ client }) => getGraphQLRequestFromClient(client)(GeoSearchDocument, variables),
  });

  const geoRes: GeoSearchOutput[] = data?.geoSearch ?? ([] as GeoSearchOutput[]);
  const geoOut: GeoLocationsList = geoRes.map(item => {
    const obj: GeoLocation = {
      city: item.city,
      code: item.code,
      continent: item.continent,
      country: item.country,
      dma: item.dma,
      id: item.id,
      latitude: `${item.latitude}`,
      longitude: `${item.longitude}`,
      msa: item.msa,
      name: item.name,
      postal_code: item.postal_code,
      region: item.region,
      state: item.state,
      type: item.type,
      'type-id': item.type_id,
    };
    return obj;
  });

  if (searchText?.length < 2) {
    return {
      geoSearchData: undefined,
      isLoading: false,
      refetch: (() => Promise.resolve({})) as GeoSearchRefetchFn,
    };
  }

  return { geoSearchData: loading ? undefined : geoOut, isLoading: loading, refetch };
};
